import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "@shared/dayjs";
import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";
import Tooltip from "@shared/v2/Tooltip";
import {
  ChevronDownSolidV6,
  ChevronLeftSolidV6,
  ChevronRightSolidV6,
  MergeSolidV6,
  PencilSolidV6,
} from "@shared/v2/Icomoon";
import { upperFirst } from "lodash";
import StatusDropdown from "@shared/StatusDropdown";
import { Popover, PopoverContent, PopoverItem, PopoverTrigger } from "@shared/v2/Popover";
import IconButton from "@shared/v2/IconButton";
import IntentDropdown from "./IntentDropdown";
import FrequencyDropdown from "./FrequencyDropdown";
import StagesDropDown from "../../shared/StagesDropDown/StagesDropDown";
import { openEditPersonModal, simpleUpdatePerson } from "../actions/personDetailActionCreators";
import Loading from "../../Goals/components/Loader";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import * as contactMergeActions from "../../reducers/contactMergeReducer";
import useIsEditable from "../shared/useIsEditable";
import PersonDetailError from "./PersonDetailError";
import RemineCard from "./RemineCard";

const InfoItem = ({ children, label }) => (
  <div className="tw-flex tw-gap-[16px] tw-items-center">
    <div className="tw-basis-5/12 tw-text-gray-50 tw-text-left tw-text-11d tw-font-semibold tw-uppercase">
      {label}:
    </div>
    <div className="tw-basis-7/12 tw-min-w-0">{children}</div>
  </div>
);

InfoItem.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
};

const PersonInfo = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const person = useSelector((state) => state.personDetail.person);
  const prevPersonSlug = useSelector((state) => state.personDetail.previousSlug);
  const nextPersonSlug = useSelector((state) => state.personDetail.nextSlug);
  const [errors, setErrors] = useState([]);
  const [showEditPopover, setShowEditPopover] = useState(false);
  const personData = person.data?.attributes || {};

  const editButtonEnabled = useIsEditable();

  const featureFlags = useFeatureFlags();

  const editValue = (key, value) => {
    setErrors([]);
    dispatch(simpleUpdatePerson({ [key]: value, id: person.data.id })).catch((err) => {
      setErrors(err.response?.data?.errors || ["An error occurred while updating the person."]);
    });
  };

  const onUpdateStage = (stage) => {
    setErrors([]);
    dispatch(simpleUpdatePerson({ stage: stage.value, stage_type: stage.type, id: person.data.id })).catch(
      (err) => {
        setErrors(err.response?.data?.errors || ["An error occurred while updating the person."]);
      },
    );
  };

  const onUpdateStatus = (status) => {
    setErrors([]);
    dispatch(simpleUpdatePerson({ status, id: person.data.id })).catch((err) => {
      setErrors(err.response?.data?.errors || ["An error occurred while updating the person."]);
    });
  };

  return (
    <div
      id="person-info-card"
      className="tw-bg-white tw-relative tw-p-[20px] tw-rounded-t-[4px] tw-shadow-card tw-flex tw-flex-col tw-gap-[16px]"
    >
      {!person.data && <Loading />}

      <div className="tw-flex tw-justify-end tw-gap-[1px]">
        <Button
          className={editButtonEnabled ? "tw-rounded-r-none" : ""}
          disabled={!editButtonEnabled}
          onClick={() => dispatch(openEditPersonModal())}
        >
          <PencilSolidV6 className="tw-mr-[4px]" size="s" />
          Edit
        </Button>
        {editButtonEnabled && (
          <Popover placement="bottom-end" offset={2} open={showEditPopover} onOpenChange={setShowEditPopover}>
            <PopoverTrigger>
              <Button
                className="tw-rounded-l-none"
                onClick={() => setShowEditPopover((o) => !o)}
                disabled={
                  !currentUser.account.isAdmin ||
                  String(currentUser.person.id) === String(person.data?.id) ||
                  person.data?.attributes?.type_name !== "lead"
                }
              >
                <ChevronDownSolidV6 size="s" />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverItem
                className="tw-whitespace-nowrap"
                onClick={() => {
                  setShowEditPopover(false);
                  dispatch(contactMergeActions.setOtherContact(null));
                  dispatch(contactMergeActions.setModalIsOpen(true));
                }}
                icon={<MergeSolidV6 />}
              >
                Merge Leads
              </PopoverItem>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="tw-flex tw-justify-between tw-items-center">
        <IconButton
          onClick={() => window.location.assign(`/people/${prevPersonSlug}`)}
          disabled={!prevPersonSlug}
        >
          <ChevronLeftSolidV6 />
        </IconButton>
        <Avatar
          src={personData.avatar_url}
          alt={`${personData.first_name || ""} ${personData.last_name || ""}`.trim()}
          size="extra-large"
        />
        <IconButton
          onClick={() => window.location.assign(`/people/${nextPersonSlug}`)}
          disabled={!nextPersonSlug}
        >
          <ChevronRightSolidV6 />
        </IconButton>
      </div>

      <h4 className="tw-m-0 tw-text-18d tw-font-bold tw-text-center">
        {`${personData.first_name || ""} ${personData.last_name || ""}`.trim() || "No Name"}
      </h4>

      <h6 className="tw-m-0 tw-text-center">
        <span className="tw-text-gray-50 tw-text-12d tw-font-semibold">SOURCE:</span>
        <span className="tw-ml-[8px] tw-text-neutral-grey-75 tw-text-12d tw-font-bold">
          {personData.source}
        </span>
      </h6>

      <Tooltip
        placement="bottom"
        trigger={
          <h6 className="tw-m-0 tw-text-center tw-text-neutral-grey-75 tw-text-12d tw-font-bold">
            Registered {dayjs(personData.submitted_at).format("MMMM Do YYYY")}
          </h6>
        }
        content={personData.submitted_at_formatted}
      />

      <div className="tw-my-0 tw-h-[1px] tw-bg-gray-10" />

      <InfoItem label="Last Interaction">
        <div
          className={`tw-text-left tw-text-[12px] tw-font-bold ${!personData.last_interacted_at ? "tw-text-[#F2714D]" : "tw-text-neutral-grey-75"
            }`}
        >
          {!personData.last_interacted_at ? "None" : dayjs(personData.last_interacted_at).fromNow()}
        </div>
      </InfoItem>

      <InfoItem label="Stage">
        {personData.type_name === "lead" ? (
          <StagesDropDown
            selectedStage={personData.stage}
            onUpdateStage={onUpdateStage}
            truncate
            containerClassName="!tw-w-auto tw-max-w-full !tw-px-0 !tw-border-0"
          />
        ) : (
          <div
            className="tw-flex tw-justify-start tw-flex-row tw-flex-nowrap tw-items-center
                  tw-text-center tw-align-middle tw-h-[24px] tw-text-neutral-gray-30 tw-text-11px tw-leading-[16px]
                  tw-font-bold"
          >
            <Tooltip placement="right" trigger="None" content="Stages are unavailable for this person type" />
          </div>
        )}
      </InfoItem>

      <InfoItem label="Brivity Status">
        <StatusDropdown
          person={person.data}
          className="tw-float-left"
          onChange={onUpdateStatus}
          statusOptions={person.meta?.status_options.filter(([, value]) => value !== "past")}
          status={person.data?.attributes?.status || "new"}
        />
      </InfoItem>

      {/* TODO: Users start of with a null lead_type. Do we want to dispaly anything */}
      {personData.lead_type && (
        <InfoItem label="Intent">
          <IntentDropdown className="tw-text-left" onUpdate={editValue} />
        </InfoItem>
      )}

      <InfoItem label="Person Type">
        <div className="tw-text-left tw-text-neutral-grey-75 tw-text-12d tw-font-bold ">
          {upperFirst(personData.type_name)}
        </div>
      </InfoItem>
      {featureFlags.sellScore && <RemineCard />}

      {errors.map((error) => (
        <PersonDetailError error={error} />
      ))}

      <div className="tw-my-0 tw-h-[1px] tw-bg-gray-10" />

      <div className="tw-text-center">
        <Checkbox
          label="Show in Leads Index?"
          checked={personData.is_lead}
          onClick={() => editValue("is_lead", !personData.is_lead)}
        />
      </div>

      {personData.granted_client_portal_access && (
        <div className="tw-text-color-999 tw-text-[10px] tw-leading-[16px] tw-font-bold">
          <div className="tw-flex tw-justify-center tw-items-center">
            <span className="tw-mr-[12px] tw-p-[4px]">UPDATE FREQUENCY?</span>
            <FrequencyDropdown />
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonInfo;
